import { paginatedQuery } from 'lib-shared-api-utils';
import { ApiResponse } from 'lib-shared-types-api';

import { api } from 'app/app.api';

import { Template } from './types/template.types';
import {
  CreateTemplateApiArg,
  CreateTemplateApiResponse,
  DeleteTemplateApiArg,
  DeleteTemplateTranslationsApiArg,
  DeleteTemplateTranslationsResponse,
  FavoriteTemplateApiArg,
  ReadAllTemplatesApiArg,
  ReadAllTemplatesApiResponse,
  ReadFavoritesApiArg,
  ReadFavoritesApiResponse,
  ReadMostUsedTemplatesApiArg,
  ReadMostUsedTemplatesApiResponse,
  ReadRecommendationsApiResponse,
  ReadRecommendationsArg,
  ReadTemplateByIdApiArg,
  ReadTemplateByIdApiResponse,
  ReadTemplatesByAutomationIdApiArg,
  ReadTemplatesByAutomationIdApiResponse,
  RenderTemplateApiArg,
  RenderTemplateApiResponse,
  TemplateLocalesApiArg,
  TemplateLocalesResponse,
  TrackTemplateUsageApiArg,
  TrackTemplateUsageResponse,
  UnfavoriteTemplateApiArg,
  UpdateTemplateArg,
  UpdateTemplateResponse,
  UpdateTemplateTranslationsArg,
  UpdateTemplateTranslationsResponse,
} from './types/templatesApi.types';

const getLocaleTranslations = (locale?: string) => {
  if (!locale || locale === 'en') {
    return undefined;
  }

  return locale;
};

/** RTKQ APIs to work with templates. */
export const templatesApi = api
  .enhanceEndpoints({
    addTagTypes: ['Templates', 'FavoriteTemplates'],
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      createTemplate: builder.mutation<
        CreateTemplateApiResponse,
        CreateTemplateApiArg
      >({
        query: (body) => {
          const { subject, ...rest } = body;

          const hasSubject = subject && subject.length > 0;

          return {
            version: 2,
            path: 'templating/templates',
            method: 'POST',
            body: {
              ...rest,
              ...(hasSubject && { subject }),
            },
          };
        },
      }),
      renderTemplate: builder.mutation<
        RenderTemplateApiResponse,
        RenderTemplateApiArg
      >({
        query: ({ csid, templateId, locale, enduserId, subject }) => ({
          version: 2,
          path: 'templating/renderings',
          method: 'POST',
          body: {
            csid,
            template_id: templateId,
            locale,
            subject,
            enduserId: enduserId,
          },
        }),
      }),
      readTemplateById: builder.query<
        ReadTemplateByIdApiResponse,
        ReadTemplateByIdApiArg
      >({
        query: ({ id }) => ({
          version: 2,
          path: `templating/templates/${encodeURIComponent(id)}`,
        }),
        providesTags: (result, error, { id }) => [{ type: 'Templates', id }],
      }),
      readAllTemplatesPaginated: builder.query<
        ReadAllTemplatesApiResponse,
        ReadAllTemplatesApiArg
      >({
        query: (arg) =>
          paginatedQuery(arg, ({ type, locale }) => ({
            version: 3,
            path: `templates`,
            method: 'GET',
            params: {
              type,
              locale_translations: getLocaleTranslations(locale),
              locale_translations_active_state:
                locale === 'en' ? undefined : 'active',
            },
          })),
      }),
      readTemplatesByAutomationId: builder.query<
        ReadTemplatesByAutomationIdApiResponse,
        ReadTemplatesByAutomationIdApiArg
      >({
        query: (arg) =>
          paginatedQuery(arg, ({ automationId }) => ({
            version: 3,
            path: `templates/automationid/${encodeURIComponent(automationId)}`,
            method: 'GET',
            params: {},
          })),
        providesTags: ['Templates'],
      }),
      readRecommendations: builder.query<
        ReadRecommendationsApiResponse,
        ReadRecommendationsArg
      >({
        query: ({ csid, locale }) => {
          return {
            version: 2,
            path: `templating/quick_response/recommendation/csid/${csid}`,
            params: {
              locale_translations: getLocaleTranslations(locale),
            },
          };
        },
        transformResponse: (
          response: ApiResponse<{
            recommendations: Array<{ score: number; template: Template }>;
          }>,
        ): ReadRecommendationsApiResponse => {
          return {
            data: {
              templates: response.data.recommendations.map(
                (rec) => rec.template,
              ),
              scores: response.data.recommendations.reduce(
                (acc, rec) => ({ ...acc, [rec.template.id]: rec.score }),
                {},
              ),
            },
            meta: {
              ...response.meta,
            },
          };
        },
      }),
      readMostUsed: builder.query<
        ReadMostUsedTemplatesApiResponse,
        ReadMostUsedTemplatesApiArg
      >({
        query: ({ locale }) => ({
          version: 3,
          path: `templates`,
          method: 'GET',
          params: {
            type: 'quick_response',
            include_most_used: true,
            locale_translations: getLocaleTranslations(locale),
            limit: 0,
          },
        }),
        transformResponse: (
          response: ReadAllTemplatesApiResponse,
        ): ReadMostUsedTemplatesApiResponse => {
          return {
            ...response,
            data: {
              templates: response.data.most_used,
            },
          };
        },
      }),
      trackTemplateUsage: builder.mutation<
        TrackTemplateUsageResponse,
        TrackTemplateUsageApiArg
      >({
        query: ({ templateId, locale, csid }) => ({
          version: 2,
          path: `templating/templates/${encodeURIComponent(
            templateId,
          )}/agent-usage`,
          method: 'POST',
          params: {
            locale_translations: getLocaleTranslations(locale),
            ...{ csid },
          },
        }),
      }),
      readFavorites: builder.query<
        ReadFavoritesApiResponse,
        ReadFavoritesApiArg
      >({
        query: ({ locale }) => {
          return {
            version: 2,
            path: `templating/templates/favorite/list`,
            params: {
              locale_translations: getLocaleTranslations(locale),
              type: 'quick_response',
            },
          };
        },
        // Unlike the other quick reply endpoints, this one ignores the
        // `locale_translations` param and always returns every favorite with
        // its full set of translations. We replicate the backend's
        // `locale_translations_active_state: 'active'` filtering here so that,
        // for a non-English locale, we only keep favorites that have an active
        // translation in the selected locale.
        transformResponse: (
          response: ReadFavoritesApiResponse,
          _meta,
          { locale },
        ): ReadFavoritesApiResponse => {
          const localeTranslations = getLocaleTranslations(locale);

          if (!localeTranslations) {
            return response;
          }

          return {
            ...response,
            data: {
              ...response.data,
              templates: response.data.templates.filter((template) => {
                const translation = template.translations?.[localeTranslations];

                return translation != null && !translation.is_deactivated;
              }),
            },
          };
        },
        providesTags: ['FavoriteTemplates'],
      }),
      favoriteTemplate: builder.mutation<void, FavoriteTemplateApiArg>({
        query: ({ id, locale }) => ({
          version: 2,
          path: `templating/templates/${encodeURIComponent(id)}/favorite`,
          method: 'PATCH',
          params: {
            locale_translations: getLocaleTranslations(locale),
          },
        }),
        invalidatesTags: ['FavoriteTemplates'],
      }),
      unfavoriteTemplate: builder.mutation<void, UnfavoriteTemplateApiArg>({
        query: ({ id, locale }) => ({
          version: 2,
          path: `templating/templates/${encodeURIComponent(id)}/unfavorite`,
          method: 'PATCH',
          params: {
            locale_translations: getLocaleTranslations(locale),
          },
        }),
        async onQueryStarted({ id, locale }, { dispatch, queryFulfilled }) {
          const patchResult = dispatch(
            templatesApi.util.updateQueryData(
              'readFavorites',
              { locale },
              (data) => {
                if (!data) return data;

                const updatedTemplates = data.data.templates.filter(
                  (template) => template.id !== id,
                );

                return {
                  ...data,
                  data: {
                    ...data.data,
                    templates: updatedTemplates,
                  },
                };
              },
            ),
          );

          try {
            await queryFulfilled;
          } catch {
            patchResult.undo();
          }
        },
      }),
      locales: builder.query<TemplateLocalesResponse, TemplateLocalesApiArg>({
        query: ({ type }) => ({
          version: 3,
          path: 'templates/locales',
          method: 'GET',
          params: {
            type,
          },
        }),
        transformResponse: (response: TemplateLocalesResponse) => {
          return {
            ...response,
            data: {
              ...response.data,
              // Backend doesn't consider 'en' as a locale, so we add it here
              locales: ['en', ...response.data.locales],
            },
          };
        },
        keepUnusedDataFor: Infinity,
      }),
      updateTemplate: builder.mutation<
        UpdateTemplateResponse,
        UpdateTemplateArg
      >({
        query: ({ id, ...body }) => ({
          version: 2,
          path: `templating/templates/${encodeURIComponent(id)}`,
          method: 'PATCH',
          body,
        }),
        invalidatesTags: (result, error, { id }) => [{ type: 'Templates', id }],
      }),
      updateTemplateTranslations: builder.mutation<
        UpdateTemplateTranslationsResponse,
        UpdateTemplateTranslationsArg
      >({
        query: (arg) => {
          const { id, translations } = arg;

          const modifiedTranslations = Object.entries(
            translations || [],
          ).reduce((currentTranslations, [locale, template]) => {
            const { isEnabled, subject, content } = template;

            const hasSubject = subject && subject.length > 0;
            const hasContent = content && content.length > 0;

            const baseTemplate = {
              content,
              ...(hasSubject && { subject }),
            };

            if (isEnabled) {
              currentTranslations[locale] = {
                is_deactivated: false,
                ...baseTemplate,
              };
            } else if (hasSubject || hasContent) {
              currentTranslations[locale] = {
                is_deactivated: true,
                ...baseTemplate,
              };
            }

            return currentTranslations;
          }, {});

          return {
            version: 2,
            path: `templating/templatetranslations/${encodeURIComponent(id)}`,
            method: 'PATCH',
            body: {
              translations: modifiedTranslations,
            },
          };
        },
        invalidatesTags: (result, error, { id }) => [{ type: 'Templates', id }],
      }),
      deleteTemplate: builder.mutation<void, DeleteTemplateApiArg>({
        query: ({ templateId }) => ({
          version: 2,
          path: `templating/templates/${encodeURIComponent(templateId)}`,
          method: 'DELETE',
        }),
        invalidatesTags: (result, error, { templateId }) => [
          { type: 'Templates', templateId },
        ],
      }),
      deleteTemplateTranslations: builder.mutation<
        DeleteTemplateTranslationsResponse,
        DeleteTemplateTranslationsApiArg
      >({
        query: ({ templateId, localesToDelete }) => ({
          version: 2,
          path: `templating/templatetranslations/${encodeURIComponent(
            templateId,
          )}`,
          method: 'DELETE',
          body: {
            localesToDelete,
          },
        }),
        invalidatesTags: (result, error, { templateId }) => [
          { type: 'Templates', templateId },
        ],
      }),
    }),
  });
